import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import { Download, Tag } from 'lucide-react';

interface Order {
  id: number;
  orderNum: string;
  createdAt: number;
  name: string;
  phone: string;
  estate: string | null;
  items: any[];
  totalPrice: number;
  deliveryDate: string;
  paymentConfirmed: number;
  orderCompleted: number;
  paymentProof?: string;
  referralCode?: string;
}

interface OrderTag {
  key: string;
  label: string;
  color: string;
}

interface Filters {
  searchTerm: string;
  deliveryDateStart: string;
  deliveryDateEnd: string;
  paymentStatus: string;
  tag: string;
  estate: string;
}

interface ServiceEstate {
  id: number;
  name: string;
}

const ALL_TAGS = [
  { key: 'new-customer', label: '新客戶' },
  { key: 'returning-customer', label: '回頭客' },
  { key: 'vip', label: 'VIP' },
  { key: 'big-order', label: '大單' },
  { key: 'standard-order', label: '標準單' },
  { key: 'next-day', label: '明日送' },
  { key: 'referred', label: '推薦單' },
];

const getOrderStatus = (order: Order) => {
  if (order.paymentConfirmed === 0) {
    return { label: '待付款', color: 'bg-red-100 text-red-700' };
  }
  if (order.paymentConfirmed === 1 && order.orderCompleted === 0) {
    return { label: '已付款', color: 'bg-orange-100 text-orange-700' };
  }
  if (order.orderCompleted === 1) {
    return { label: '訂單完成', color: 'bg-green-100 text-green-700' };
  }
  return { label: '未知', color: 'bg-gray-100 text-gray-700' };
};

const getOrderTags = (order: Order, allOrders: Order[]): OrderTag[] => {
  const tags: OrderTag[] = [];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  // Customer type based on phone
  const customerOrders = allOrders.filter(o => o.phone && o.phone === order.phone);
  const orderCount = customerOrders.length;
  if (orderCount >= 3) {
    tags.push({ key: 'vip', label: 'VIP', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' });
  } else if (orderCount >= 2) {
    tags.push({ key: 'returning-customer', label: '回頭客', color: 'bg-purple-100 text-purple-700 border-purple-200' });
  } else {
    tags.push({ key: 'new-customer', label: '新客戶', color: 'bg-blue-100 text-blue-700 border-blue-200' });
  }

  // Order amount
  if (order.totalPrice >= 500) {
    tags.push({ key: 'big-order', label: '大單', color: 'bg-amber-100 text-amber-700 border-amber-200' });
  } else if (order.totalPrice >= 300) {
    tags.push({ key: 'standard-order', label: '標準單', color: 'bg-green-100 text-green-700 border-green-200' });
  }

  // Delivery timing
  if (order.deliveryDate === tomorrow) {
    tags.push({ key: 'next-day', label: '明日送', color: 'bg-orange-100 text-orange-700 border-orange-200' });
  }

  // Referral
  if (order.referralCode) {
    tags.push({ key: 'referred', label: '推薦單', color: 'bg-teal-100 text-teal-700 border-teal-200' });
  }

  return tags;
};

const formatItems = (itemsRaw: any[]): string => {
  if (!itemsRaw || itemsRaw.length === 0) return '-';
  try {
    const items = typeof itemsRaw === 'string' ? JSON.parse(itemsRaw) : itemsRaw;
    if (!Array.isArray(items)) return '-';
    return items.map((item: any) => {
      // New format: individual items with name
      if (item.name) {
        return `${item.name} x${item.quantity || 1}`;
      }
      // Old format: package type
      if (item.packageType) {
        return `${item.packageType === '2-dish-1-soup' ? '2餸1湯' : '3餸1湯'} x${item.quantity || 1}`;
      }
      // Fallback
      return `${item.productId || item.dishName || '商品'} x${item.quantity || 1}`;
    }).join(', ');
  } catch {
    return '-';
  }
};

interface ProductItem {
  id: number;
  name: string;
  category: string;
}

interface ManualTag {
  id: number;
  tag: string;
  color: string;
}

const TAG_COLORS: Record<string, string> = {
  gray: 'bg-gray-100 text-gray-700 border-gray-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  pink: 'bg-pink-100 text-pink-700 border-pink-200',
};

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [manualTagsMap, setManualTagsMap] = useState<Record<string, ManualTag[]>>({});
  const [filters, setFilters] = useState<Filters>({
    searchTerm: '',
    deliveryDateStart: '',
    deliveryDateEnd: '',
    paymentStatus: '',
    tag: '',
    estate: ''
  });
  const [estates, setEstates] = useState<ServiceEstate[]>([]);
  const navigate = useNavigate();
  const { can, user } = usePermissions();
  const isEstateManager = user?.role === 'estate_manager';

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchOrders();
    if (can(PERMISSIONS.PRODUCTS_READ)) fetchProducts();
    if (can(PERMISSIONS.CUSTOMERS_READ)) fetchManualTags();
    if (can(PERMISSIONS.ESTATES_READ)) fetchEstates();
    // Check URL query for phone filter from customer list
    const params = new URLSearchParams(window.location.search);
    const phone = params.get('phone');
    if (phone) {
      setFilters(prev => ({ ...prev, searchTerm: phone }));
    }
  }, []);

  const fetchProducts = async () => {
    const res = await apiFetch('/api/public/admin/products');
    if (res.ok) {
      const data = await res.json();
      setProducts(data.filter((p: any) => p.category === 'dish' || p.category === 'soup' || p.category === 'item'));
    }
  };

  const fetchEstates = async () => {
    const res = await apiFetch('/api/public/admin/service-estates');
    if (res.ok) {
      const data = await res.json();
      setEstates(data);
    }
  };

  const fetchManualTags = async () => {
    try {
      const res = await apiFetch('/api/public/admin/customers');
      if (res.ok) {
        const customers = await res.json();
        const map: Record<string, ManualTag[]> = {};
        for (const c of customers) {
          map[c.phone] = c.manualTags || [];
        }
        setManualTagsMap(map);
      }
    } catch (e) {
      console.error('Failed to fetch manual tags:', e);
    }
  };

  const fetchOrders = async () => {
    const res = await apiFetch('/api/public/admin/orders');
    if (res.ok) {
      setOrders(await res.json());
    } else {
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除此訂單嗎？')) return;
    const res = await apiFetch(`/api/public/admin/orders/${id}`, { 
      method: 'DELETE'
    });
    if (res.ok) fetchOrders();
  };

  const handleComplete = async (id: number) => {
    const res = await apiFetch(`/api/public/admin/orders/${id}/complete`, {
      method: 'POST'
    });
    if (res.ok) {
      fetchOrders();
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getReferrer = (order: Order) => {
    if (!order.referralCode) return '-';
    const referrer = orders.find(o => o.phone && o.phone.slice(-4) === order.referralCode && o.id !== order.id);
    return referrer ? referrer.name : '-';
  };

  const clearFilters = () => {
    setFilters({ searchTerm: '', deliveryDateStart: '', deliveryDateEnd: '', paymentStatus: '', tag: '', estate: '' });
  };

  const exportToCSV = () => {
    const dishProducts = products.filter(p => p.category === 'dish').map(p => p.name);
    const soupProducts = products.filter(p => p.category === 'soup').map(p => p.name);
    const itemProducts = products.filter(p => p.category === 'item').map(p => p.name);

    const baseHeaders = ['訂單號', '下單日期', '姓名', '電話', '屋苑', '配送日期', '總額(HK$)', '付款狀態', '訂單狀態', '標籤'];
    const productHeaders = [...dishProducts, ...soupProducts, ...itemProducts];
    const headers = [...baseHeaders, ...productHeaders];

    const rows = filteredOrders.map(order => {
      const status = getOrderStatus(order);
      const tags = getOrderTags(order, orders);
      const tagLabels = tags.map(t => t.label).join(';');

      const productCounts: Record<string, number> = {};
      for (const p of productHeaders) productCounts[p] = 0;

      try {
        const items = JSON.parse(order.items || '[]');
        for (const item of items) {
          const qty = item.quantity || 1;
          // New format: item.name
          if (item.name && productCounts[item.name] !== undefined) {
            productCounts[item.name] += qty;
          }
          // Old format: selectedDishes + selectedSoup
          for (const dishName of (item.selectedDishes || [])) {
            if (productCounts[dishName] !== undefined) {
              productCounts[dishName] += qty;
            }
          }
          if (item.selectedSoup && productCounts[item.selectedSoup] !== undefined) {
            productCounts[item.selectedSoup] += qty;
          }
        }
      } catch { /* ignore */ }

      const baseRow = [
        order.orderNum || "-",
        new Date(order.createdAt * 1000).toLocaleDateString('zh-HK'),
        order.name,
        order.phone,
        order.estate || '',
        order.deliveryDate,
        order.totalPrice,
        order.paymentConfirmed === 1 ? '已付款' : order.paymentProof ? '待審核' : '未付款',
        status.label,
        tagLabels,
      ];

      const productRow = productHeaders.map(name => productCounts[name] || 0);
      return [...baseRow, ...productRow];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `orders_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredOrders = orders.filter(order => {
    const orderNum = (order.orderNum || "-");
    const searchLower = filters.searchTerm.toLowerCase();
    const deliveryDateMatch = !filters.deliveryDateStart && !filters.deliveryDateEnd || (
      order.deliveryDate &&
      (!filters.deliveryDateStart || order.deliveryDate >= filters.deliveryDateStart) &&
      (!filters.deliveryDateEnd || order.deliveryDate <= filters.deliveryDateEnd)
    );
    const paymentStatusMatch = !filters.paymentStatus || (
      filters.paymentStatus === 'paid' ? order.paymentConfirmed === 1 :
      filters.paymentStatus === 'unpaid' ? order.paymentConfirmed === 0 :
      filters.paymentStatus === 'pending_proof' ? (order.paymentConfirmed === 0 && order.paymentProof) :
      true
    );
    const tagMatch = !filters.tag || (
      getOrderTags(order, orders).some(t => t.key === filters.tag)
    );
    const estateMatch = !filters.estate || order.estate === filters.estate;

    return (
      (orderNum.includes(filters.searchTerm) ||
      String(order.id).includes(filters.searchTerm) ||
      (order.name && order.name.toLowerCase().includes(searchLower)) ||
      (order.phone && order.phone.includes(filters.searchTerm)) ||
      (order.estate && order.estate.includes(filters.searchTerm)) ||
      (order.items && JSON.stringify(order.items).toLowerCase().includes(searchLower))) &&
      deliveryDateMatch &&
      paymentStatusMatch &&
      tagMatch &&
      estateMatch
    );
  });

  const hasActiveFilters = filters.searchTerm || filters.deliveryDateStart || filters.deliveryDateEnd || filters.paymentStatus || filters.tag || filters.estate;

  return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
          <div className="md:col-span-1 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋</label>
            <input
              type="text"
              placeholder="訂單號、姓名、電話..."
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={filters.searchTerm}
              onChange={e => handleFilterChange('searchTerm', e.target.value)}
            />
          </div>
          <div className="md:col-span-1 lg:col-span-2">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">配送日期範圍</label>
            <div className="flex gap-2">
              <input
                type="date"
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
                value={filters.deliveryDateStart}
                onChange={e => handleFilterChange('deliveryDateStart', e.target.value)}
              />
              <span className="flex items-center text-gray-400 px-1">~</span>
              <input
                type="date"
                className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
                value={filters.deliveryDateEnd}
                onChange={e => handleFilterChange('deliveryDateEnd', e.target.value)}
              />
            </div>
          </div>
          <div className="md:col-span-1 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">標籤</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={filters.tag}
              onChange={e => handleFilterChange('tag', e.target.value)}
            >
              <option value="">全部標籤</option>
              {ALL_TAGS.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">付款狀態</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={filters.paymentStatus}
              onChange={e => handleFilterChange('paymentStatus', e.target.value)}
            >
              <option value="">全部狀態</option>
              <option value="paid">已付款</option>
              <option value="unpaid">未付款</option>
              <option value="pending_proof">待審核</option>
            </select>
          </div>
          <div className="md:col-span-1 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">屋苑</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={filters.estate}
              onChange={e => handleFilterChange('estate', e.target.value)}
            >
              <option value="">全部屋苑</option>
              {estates.map(e => (
                <option key={e.id} value={e.name}>{e.name}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 lg:col-span-2 flex items-center justify-end gap-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              共 <span className="font-bold text-gray-800 dark:text-white">{filteredOrders.length}</span> 筆
              {hasActiveFilters && <span className="text-orange-600 text-xs ml-1">（已篩選）</span>}
            </div>
            <button
              onClick={exportToCSV}
              disabled={filteredOrders.length === 0}
              className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium px-2 py-1 border border-green-200 rounded hover:bg-green-50 disabled:opacity-40"
              title="導出當前篩選結果為 CSV"
            >
              <Download size={14} /> CSV
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-xs text-orange-600 hover:text-orange-800 font-medium px-2 py-1 border border-orange-200 rounded hover:bg-orange-50"
              >
                清除
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <th className="p-4">ID</th>
              <th className="p-4">訂單號</th>
              <th className="p-4">下單日期</th>
              <th className="p-4">姓名</th>
              <th className="p-4">電話</th>
              <th className="p-4">屋苑</th>
              <th className="p-4">產品</th>
              <th className="p-4">總額</th>
              <th className="p-4">配送日期</th>
              <th className="p-4">訂單狀態</th>
              <th className="p-4">標籤</th>
              <th className="p-4">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredOrders.map(order => {
              const status = getOrderStatus(order);
              const tags = getOrderTags(order, orders);
              return (
                <tr key={order.id} className="hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="p-4 text-sm text-gray-600 dark:text-gray-400">{order.id}</td>
                  <td className="p-4 font-mono font-bold text-orange-600 text-sm">{(order.orderNum || "-")}</td>
                  <td className="p-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{new Date(order.createdAt * 1000).toLocaleDateString('zh-HK')}</td>
                  <td className="p-4 text-sm font-medium text-gray-800 dark:text-gray-300">{order.name}</td>
                  <td className="p-4 text-sm text-gray-600 dark:text-gray-400">{order.phone}</td>
                  <td className="p-4 text-sm text-gray-600 dark:text-gray-400">{order.estate || '-'}</td>
                  <td className="p-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs">
                    {formatItems(order.items)}
                  </td>
                  <td className="p-4 text-sm font-bold text-gray-800 dark:text-white">HK${order.totalPrice}</td>
                  <td className="p-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{order.deliveryDate}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                      {status.label}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {tags.map(tag => (
                        <span
                          key={tag.key}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${tag.color} cursor-pointer hover:opacity-80`}
                          onClick={() => handleFilterChange('tag', tag.key)}
                          title="點擊篩選此標籤"
                        >
                          <Tag size={10} className="mr-0.5" />
                          {tag.label}
                        </span>
                      ))}
                      {(manualTagsMap[order.phone] || []).map(mtag => (
                        <span
                          key={`m-${mtag.id}`}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${TAG_COLORS[mtag.color] || TAG_COLORS.gray}`}
                          title="手動標籤"
                        >
                          {mtag.tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-2">
                      {!isEstateManager && (
                        <button onClick={() => navigate(`/admin/orders/${order.id}`)} className="bg-blue-500 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-600 transition-colors">詳情</button>
                      )}
                      {order.paymentConfirmed === 1 && order.orderCompleted === 0 && can(PERMISSIONS.ORDERS_COMPLETE) && (
                        <button onClick={() => handleComplete(order.id)} className="bg-green-500 text-white px-3 py-1.5 rounded text-xs hover:bg-green-600 transition-colors">標記完成</button>
                      )}
                      {can(PERMISSIONS.ORDERS_DELETE) && (
                        <button onClick={() => handleDelete(order.id)} className="bg-red-500 text-white px-3 py-1.5 rounded text-xs hover:bg-red-600 transition-colors">刪除</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredOrders.length === 0 && (
              <tr>
                <td colSpan={12} className="p-8 text-center text-gray-500 dark:text-gray-400">
                  {orders.length === 0 ? '暫無訂單' : '沒有找到相符的訂單'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
